import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useBeforeUnload, useBlocker, useNavigate } from 'react-router-dom';
import { AppHeaderContent } from './AppHeaderContent';
import { ModalOverlay } from './ModalOverlay';
import { extractApiMessage } from '@/lib/api';

export function MobileFormPage({
  title,
  backTo,
  backLabel = 'Camada',
  saveLabel = 'Guardar cambios',
  dirty,
  disabled = false,
  externalBusy = false,
  onSave,
  children,
}: {
  title: string;
  backTo: string;
  backLabel?: string;
  saveLabel?: string;
  dirty: boolean;
  disabled?: boolean;
  externalBusy?: boolean;
  onSave: () => Promise<string | undefined>;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [savingState, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  const dirtyRef = useRef(dirty),
    saving = useRef(false),
    errorRef = useRef<HTMLDivElement>(null);
  dirtyRef.current = dirty;
  const externalBusyRef = useRef(externalBusy);
  externalBusyRef.current = externalBusy;
  const busy = savingState || externalBusy;
  const blocker = useBlocker(() => dirtyRef.current || saving.current || externalBusyRef.current);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useBeforeUnload((event) => {
    if (dirtyRef.current || saving.current || externalBusyRef.current) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  async function submit() {
    if (disabled || saving.current || externalBusyRef.current) return;
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      const destination = await onSave();
      if (destination) {
        dirtyRef.current = false;
        saving.current = false;
        if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
        navigate(destination, { replace: true });
      }
    } catch (err) {
      setError(extractApiMessage(err));
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }
  return (
    <main className="cm-form-page mx-auto max-w-md px-4">
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent backTo={backTo} backLabel={backLabel} />
      </header>
      <h1 className="ui-page-title">{title}</h1>
      {error && (
        <div className="parte-error" role="alert" ref={errorRef} tabIndex={-1}>
          {error}
        </div>
      )}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <fieldset disabled={busy} className="cm-form-fields">
          {children}
        </fieldset>
        <footer className="cm-form-actions">
          <Link className="ui-secondary" to={backTo}>
            Cancelar
          </Link>
          <button type="submit" className="ui-primary" disabled={disabled || busy}>
            {busy ? 'Guardando…' : saveLabel}
          </button>
        </footer>
      </form>
      {blocker.state === 'blocked' && (
        <ModalOverlay
          ariaLabel="Cambios sin guardar"
          placement="bottom"
          className="max-w-md"
          onBackdrop={() => blocker.reset()}
        >
          <h2 className="ui-page-title">{busy ? 'Guardando…' : 'Cambios sin guardar'}</h2>
          <p className="py-3">
            {busy
              ? 'Espera a que termine el guardado antes de salir.'
              : 'Puedes seguir editando o salir sin guardar este borrador.'}
          </p>
          <button className="ui-primary w-full" onClick={() => blocker.reset()}>
            Seguir editando
          </button>
          {!busy && (
            <button
              className="ui-secondary mt-2 w-full"
              onClick={() => {
                dirtyRef.current = false;
                blocker.proceed();
              }}
            >
              Salir sin guardar
            </button>
          )}
        </ModalOverlay>
      )}
    </main>
  );
}
